import type { TFunction } from 'i18next';

import { formatMoney, formatNumber } from '@/utils/format';

import type { CustomReportFieldDefinition } from './custom-report-sources';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-7][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const looksLikeOpaqueId = (field: CustomReportFieldDefinition | undefined, text: string): boolean =>
  Boolean(
    (field?.filterOnly || field?.key === 'id' || field?.key.endsWith('Id')) &&
      UUID_PATTERN.test(text),
  );

export const formatCustomReportCell = (
  field: CustomReportFieldDefinition | undefined,
  value: unknown,
  row: Record<string, unknown>,
  t: TFunction<'reports'>,
  locale: string,
): string => {
  if (value === null || value === undefined || value === '') return '—';

  const moneyCurrency =
    (typeof row.currency === 'string' && row.currency) ||
    (typeof row.acquisitionCurrency === 'string' && row.acquisitionCurrency) ||
    (typeof row.chargeCurrency === 'string' && row.chargeCurrency) ||
    'GHS';

  if (
    field?.type === 'number' &&
    typeof value === 'number' &&
    field.key.endsWith('Minor')
  ) {
    return formatMoney(value, moneyCurrency);
  }

  if (field?.key === 'amountMinor' && typeof value === 'number') {
    return formatMoney(value, moneyCurrency);
  }

  if (field?.type === 'boolean') {
    return value === true || value === 'true' ? t('custom.value.yes') : t('custom.value.no');
  }

  if (field?.type === 'number' && typeof value === 'number') {
    return formatNumber(value, locale);
  }

  if (field?.valueSet) {
    return t(`custom.enums.${field.valueSet}.${String(value)}`, String(value));
  }

  if (typeof value === 'string' && looksLikeOpaqueId(field, value)) return '—';

  if (typeof value === 'string' || typeof value === 'number') return String(value);
  return JSON.stringify(value);
};
